import React, { useState } from 'react';
import { Send, MessageSquare, ArrowLeft } from 'lucide-react';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useAuth } from '../contexts/AuthContext';
import SEOHead from '../components/SEOHead';
import BreadcrumbSchema from '../components/BreadcrumbSchema';

const Feedback: React.FC = () => {
  const [feedback, setFeedback] = useState('');
  const [category, setCategory] = useState('general');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { currentUser } = useAuth();

  const categories = [
    { value: 'general', label: 'General Feedback' },
    { value: 'bug', label: 'Bug Report' },
    { value: 'feature', label: 'Feature Request' },
    { value: 'performance', label: 'Performance Issue' },
    { value: 'ui', label: 'UI/UX Feedback' }
  ];

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!feedback.trim()) {
      toast.error('Please enter your feedback');
      return;
    }

    if (!currentUser) {
      toast.error('Please sign in to submit feedback');
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await fetch('/api/v1/feedback', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${await currentUser.getIdToken()}`
        },
        body: JSON.stringify({
          feedback: feedback.trim(),
          category,
          userAgent: navigator.userAgent,
          url: window.location.href
        })
      });

      if (!response.ok) {
        throw new Error('Failed to submit feedback');
      }

      toast.success('Thank you for your feedback! We appreciate it.');
      setFeedback('');
      setCategory('general');
    } catch (error) {
      console.error('Failed to submit feedback:', error);
      toast.error('Failed to submit feedback. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <SEOHead 
        title="Send Feedback - ClipThis"
        description="Share your thoughts and help us improve ClipThis. We value your feedback and suggestions."
      />
      <BreadcrumbSchema items={[
        { name: 'Feedback', path: '/feedback' }
      ]} />
      
      <div className="relative overflow-x-hidden" style={{ minHeight: 'calc(100vh - 64px)' }}>
        {/* Subtle animated background */}
        <div className="absolute inset-0 dot-pattern-animated opacity-30 pointer-events-none" />
        
        <div className="px-6 sm:px-8 py-4 relative z-10 animate-fade-up">
          {/* Back Button */}
          <Link 
            to={currentUser ? "/dashboard" : "/"}
            className="inline-flex items-center space-x-2 text-primary-600 hover:text-primary-700 transition-colors mb-8"
          >
            <ArrowLeft size={20} />
            <span>Back to {currentUser ? "Dashboard" : "Home"}</span>
          </Link>
        
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="text-center mb-12">
            <div className="w-16 h-16 bg-red-600 rounded-2xl flex items-center justify-center mx-auto mb-6">
              <MessageSquare size={32} className="text-white" />
            </div>
            <h1 className="text-4xl font-bold text-gray-900 mb-4">Send <span className="text-red-600">Feedback</span></h1>
            <p className="text-xl text-primary-600 max-w-2xl mx-auto">
              Help us improve ClipThis by sharing your thoughts, suggestions, or reporting issues. 
              We read every piece of feedback and appreciate your input.
            </p>
          </div>

          {/* Main Content */}
          <div className="bg-white rounded-3xl shadow-lifted border border-primary-100 overflow-hidden">
            <div className="p-8 md:p-12">
              <form onSubmit={handleSubmit} className="space-y-8">
                {/* Category Selection */}
                <div>
                  <label className="block text-lg font-semibold text-gray-900 mb-3">
                    What type of feedback are you sharing?
                  </label>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                    {categories.map((cat) => (
                      <label key={cat.value} className="relative">
                        <input
                          type="radio"
                          name="category"
                          value={cat.value}
                          checked={category === cat.value}
                          onChange={(e) => setCategory(e.target.value)}
                          className="sr-only"
                          disabled={isSubmitting}
                        />
                        <div className={`p-4 rounded-xl border-2 cursor-pointer transition-all duration-200 ${
                          category === cat.value
                            ? 'border-primary-500 bg-primary-50 shadow-sm'
                            : 'border-primary-200 hover:border-primary-300 hover:bg-primary-25'
                        }`}>
                          <span className="font-medium text-gray-900">{cat.label}</span>
                        </div>
                      </label>
                    ))}
                  </div>
                </div>

                {/* Feedback Text */}
                <div>
                  <label className="block text-lg font-semibold text-gray-900 mb-3">
                    Your Feedback
                  </label>
                  <textarea
                    value={feedback}
                    onChange={(e) => setFeedback(e.target.value)}
                    placeholder="Tell us what you think, what's working well, or what could be improved..."
                    className="w-full px-4 py-4 text-lg border-2 border-primary-200 rounded-xl focus:outline-none focus:ring-4 focus:ring-red-100 focus:border-red-500 resize-none transition-all duration-200"
                    rows={6}
                    disabled={isSubmitting}
                    maxLength={1000}
                  />
                  <div className="flex justify-between items-center mt-3">
                    <p className="text-sm text-primary-600">
                      💡 Tip: Include specific details to help us understand your experience better
                    </p>
                    <span className="text-sm text-primary-500 font-medium">
                      {feedback.length}/1000
                    </span>
                  </div>
                </div>

                {/* Submit Button */}
                <div className="flex flex-col sm:flex-row gap-4 pt-4">
                  <button
                    type="submit"
                    disabled={isSubmitting || !feedback.trim()}
                    className="flex-1 bg-red-600 hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed text-white font-semibold py-4 px-8 rounded-xl transition-all duration-200 flex items-center justify-center space-x-3 text-lg shadow-sm hover:shadow-md"
                  >
                    {isSubmitting ? (
                      <>
                        <div className="w-5 h-5 border-2 border-white/20 border-t-white rounded-full animate-spin" />
                        <span>Sending...</span>
                      </>
                    ) : (
                      <>
                        <Send size={20} />
                        <span>Send Feedback</span>
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>

          {/* Additional Info */}
          <div className="mt-12 text-center">
            <div className="bg-white/60 backdrop-blur-sm rounded-2xl p-8 border border-red-100">
              <h3 className="text-xl font-semibold text-gray-900 mb-4">What happens <span className="text-red-600">next?</span></h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 text-sm text-primary-700">
                <div>
                  <div className="w-8 h-8 bg-red-100 rounded-lg flex items-center justify-center mx-auto mb-3">
                    <span className="font-bold text-red-600">1</span>
                  </div>
                  <p><strong>We read it</strong><br />Every feedback submission is reviewed by our team</p>
                </div>
                <div>
                  <div className="w-8 h-8 bg-red-100 rounded-lg flex items-center justify-center mx-auto mb-3">
                    <span className="font-bold text-red-600">2</span>
                  </div>
                  <p><strong>We prioritize</strong><br />Important issues and popular requests get attention first</p>
                </div>
                <div>
                  <div className="w-8 h-8 bg-red-100 rounded-lg flex items-center justify-center mx-auto mb-3">
                    <span className="font-bold text-red-600">3</span>
                  </div>
                  <p><strong>We improve</strong><br />Your feedback directly influences our product roadmap</p>
                </div>
              </div>
            </div>
          </div>
        </div>
        </div>
      </div>
    </>
  );
};

export default Feedback; 